import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumbs from './BreadCrumbs';
import { fetchCaseHistoryRequest, formatDate } from '@rb-serve-app/core';
import Spinner from './Spinner';
import moment from 'moment';
import { formatStringToTitleCase } from '../utility/helpers';
import SearchBar from './CommonComponents/SearchBar';
import { NoServiceRequestIcon } from '../assets/svg/ViewEPPIcon';
import { logBrazeCustomEvent } from '../config/braze';

const ViewServiceRequest = () => {
  const dispatch = useDispatch();
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );

  const { accountId, customerId } = useSelector((state: any) => state.user);
  const { caseHistoryLoading, caseHistory } = useSelector(
    (state: any) => state.profile
  );
  const rb_cases_content = contentfulData?.entries?.rb_view_service_request;

  useEffect(() => {
    dispatch(fetchCaseHistoryRequest({ accountId, customerId }));
  }, [dispatch, accountId, customerId]);

  useEffect(() => {
    logBrazeCustomEvent('viewed_service_requests');
  }, []);

  const breadcrumbs = [
    {
      name: contentfulData?.entries?.rb_navigation?.homeLogoTitle || 'Home',
      href: '/home',
    },
    { name: rb_cases_content?.breadcrumbTile, href: '/service-request' },
  ];
  const TableHeader = () => {
    return (
      <thead className="border-b">
        <tr>
          <th className="text-base  py-2 px-4 border-b text-left text-[#666666] font-normal hidden md:table-cell w-[15%] lg:w-[15%] md:w-[25%]">
            {rb_cases_content?.tableHeader?.date}
          </th>
          <th className="text-base py-2 px-4 border-b text-left text-[#666666] font-normal w-[40%]">
            {rb_cases_content?.tableHeader?.request_type}
          </th>

          <th className="text-base py-2 px-4 border-b text-left text-[#666666] font-normal hidden md:table-cell xl:w-[15%] lg:w-[21%] md:w-[15%]">
            {rb_cases_content?.tableHeader?.confirmation_no}
          </th>
          <th className="text-base py-2 px-4 border-b text-left text-[#666666] font-normal hidden md:table-cell w-[15%]">
            {rb_cases_content?.tableHeader?.status}
          </th>
        </tr>
      </thead>
    );
  };

  const [cases, setCases] = useState([]);
  const [query, setQuery] = useState('');
  const [filterRequestType, setfilterRequestType] = useState([]);
  const lang = localStorage.getItem('language') || 'en-US';
  useEffect(() => {
    const requestTypeResult = caseHistory?.map((type: any) => {
      const requestTypes = rb_cases_content?.requestTypes;
      return {
        ...type,
        requestType: requestTypes?.[type.requestType] ?? type.requestType, // fallback to original
      };
    });
    setfilterRequestType(requestTypeResult || []);
  }, [caseHistory, rb_cases_content?.requestTypes]);

  const handleSearch = (searchKey: string) => {
    setQuery(searchKey);
    console.log('Search query:', searchKey);
  };
  useEffect(() => {
    if (filterRequestType) {
      const filteredCases = filterRequestType.filter(
        (caseItem: any) =>
          // caseItem.requestType
          //   .replace(/_/g, ' ')
          //   .toLowerCase()
          //   .includes(query.toLowerCase()) ||
          caseItem.status.toLowerCase().includes(query.toLowerCase()) ||
          caseItem.confirmationNumber
            .toLowerCase()
            .includes(query.toLowerCase()) ||
          moment(caseItem.requestDate)
            .format('MMM D, YYYY')
            .toLowerCase()
            .includes(query.toLowerCase())
      );
      setCases(filteredCases);
    }
  }, [query, filterRequestType]);

  type CardProps = {
    date: string;
    confirmationNumber: string;
    requestType: string;
    status: string;
  };

  const CardView: React.FC<CardProps> = ({
    date,
    confirmationNumber,
    requestType,
    status,
  }) => {
    const statusClasses =
      status.toLocaleLowerCase() === 'approved'
        ? 'text-status-approved-color'
        : status.toLocaleLowerCase() === 'pending'
          ? 'text-status-pending-color'
          : 'text-status-approved-color';

    return (
      <div className="py-4 bg-white border-b-2 border-light-gray-border">
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-col">
            <div className="text-sm text-gray-500">
              {' '}
              {rb_cases_content?.tableHeader?.date}
            </div>
            <div className="text-sm font-medium text-gray-900">{date}</div>
          </div>
          <div className="flex flex-col">
            <div className="text-sm text-gray-500 mt-2">
              {rb_cases_content?.tableHeader?.confirmation_no}
            </div>
            <div className="text-sm font-medium text-gray-900">
              {confirmationNumber}
            </div>
          </div>
        </div>
        <div className="text-sm text-gray-500 mt-2">
          {rb_cases_content?.tableHeader?.request_type}
        </div>
        <div className="text-sm font-medium text-gray-900">{requestType}</div>
        <div className="text-sm text-gray-500 mt-2">
          {rb_cases_content?.tableHeader?.status}
        </div>
        <div
          className={`inline-block  text-sm font-medium rounded ${statusClasses}`}
        >
          {status}
        </div>
      </div>
    );
  };
  const NoServiceReq = () => {
    return (
      <div
        className="flex justify-center items-center h-[50vh]"
        aria-live="polite"
        tabIndex={0}
      >
        <div className="flex flex-col justify-center items-center">
          <NoServiceRequestIcon />
          <p className="text-[#666666] font-[500] text-base text-center">
            {rb_cases_content?.noDataTitle}
          </p>
          <p className="text-[#666666] font-normal text-base text-center">
            {rb_cases_content?.noDataDesc}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-[#fafafa] flex flex-col py-4 px-8">
        <Breadcrumbs items={breadcrumbs} />
      </div>
      <div className="bg-yellow lg:px-4 md:px-8 px-0">
        <div className="w-full mx-auto rounded-lg bg-white min-h-[70vh] p-4 md:p-6 md:shadow-lg shadow-none">
          <h1
            className="text-lg md:text-2xl font-semibold text-gray-800 mb-1 md:mb-4 text-left px-4 md:px-0"
            tabIndex={0}
            aria-label="My Service Requests"
          >
            {rb_cases_content?.tableHeader?.module_title}
          </h1>
          <div className="px-4 py-0 md:py-4">
            <SearchBar
              placeholder={rb_cases_content?.searchPlaceholder}
              onSearch={handleSearch}
              className="w-full"
              buttonLabel="Search"
            />
          </div>

          {caseHistoryLoading ? (
            <div
              className="flex justify-center items-center h-[50vh]"
              aria-live="polite"
              aria-busy="true"
              role="status"
            >
              <Spinner aria-label="Loading service requests" />
            </div>
          ) : (
            cases && (
              <>
                <div className="overflow-x-auto  hidden md:block">
                  <table
                    className="min-w-full border-collapse text-sm md:text-base"
                    aria-label="Service request history table"
                  >
                    {cases.length === 0 ? <NoServiceReq /> : <TableHeader />}
                    <tbody>
                      {cases.map((request: any, index: number) => (
                        <tr key={index} className="cursor-pointer group">
                          <td className="text-base font-[500] py-2 px-4 border-b hidden md:table-cell lg:table-cell group-hover:bg-[#f5f5f5] text-[#1F1F1F]">
                            {moment(request.requestDate)
                              .locale(lang)
                              .format('MMM D, YYYY')}
                          </td>
                          <td className="text-base  font-[500] py-2 px-4 border-b hidden md:table-cell lg:table-cell group-hover:bg-[#f5f5f5] text-[#1F1F1F]">
                            {request?.requestType}
                          </td>
                          <td className="text-base  font-[500] py-2 px-4 border-b hidden md:table-cell lg:table-cell group-hover:bg-[#f5f5f5] text-[#1F1F1F]">
                            {request.confirmationNumber}
                          </td>
                          <td
                            className={`text-base  font-[500] py-2 px-4 border-b hidden  md:table-cell lg:table-cell group-hover:bg-[#f5f5f5] text-[#1F1F1F] ${
                              request.status.toLocaleLowerCase() === 'approved'
                                ? 'text-status-approved-color'
                                : request.status.toLocaleLowerCase() ===
                                    'pending'
                                  ? 'text-status-pending-color'
                                  : 'text-status-cancelled-color'
                            }`}
                          >
                            {request.status === 'APPROVED'
                              ? rb_cases_content?.tableHeader?.approved_status
                              : request.status === 'PENDING'
                                ? rb_cases_content?.tableHeader?.pending_status
                                : rb_cases_content?.tableHeader
                                    ?.cancelled_status}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2 min-h-screen block md:hidden">
                  {cases.map((request: any, index: number) => (
                    <CardView
                      key={index}
                      date={moment(request.requestDate)
                        .locale(lang)
                        .format('MMM D, YYYY')}
                      confirmationNumber={request.confirmationNumber}
                      requestType={formatStringToTitleCase(request.requestType)}
                      status={formatStringToTitleCase(request.status)}
                    />
                  ))}
                  {cases.length === 0 && <NoServiceReq />}
                </div>
              </>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default ViewServiceRequest;
